import React, { useState, useEffect, useCallback } from "react";
import {
  getAllGames,
  getAllAssignments,
  getAssignmentMetrics,
  updateAssignmentStatus,
  removeAssignment,
  remindAssignments,
  setGamePublicPlaytest,
} from "../../services/adminAssignmentService";
import {
  getPlaytestTypeLabel,
  isAdminAssignablePlaytest,
  isPublicPlaytestGame,
  resolvePlaytestTypeId,
  getGamePlaytime,
} from "../../utils/playtestTypes";
import "./GameAssignmentsManager.scss";

const PLAY_PROGRESS_LABELS = {
  played: "Played",
  not_started: "Not started",
  in_progress: "In progress",
  completed: "Played",
  cancelled: "Cancelled",
};

function derivePlayProgress(assignment) {
  if (assignment.playProgress) return assignment.playProgress;
  const status = assignment.status || "assigned";
  const sessions = Number(assignment.sessionsCompleted || 0);
  if (status === "cancelled") return "cancelled";
  if (status === "completed" || sessions > 0) return "played";
  if (status === "in_progress") return "in_progress";
  return "not_started";
}

/**
 * Game Assignments Manager Component
 * View and manage playtester assignments for games
 */
const GameAssignmentsManager = () => {
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [playProgressStats, setPlayProgressStats] = useState({
    played: 0,
    notStarted: 0,
    inProgress: 0,
  });
  const [globalMetrics, setGlobalMetrics] = useState(null);
  const [progressFilter, setProgressFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [publicPlaytestUpdating, setPublicPlaytestUpdating] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [remindModal, setRemindModal] = useState(null);
  const [remindMessage, setRemindMessage] = useState("");
  const [remindForce, setRemindForce] = useState(false);
  const [reminding, setReminding] = useState(false);

  const getGameId = (game) => game?._id || game?.gameId || game?.id || null;

  const fetchMetrics = useCallback(async () => {
    try {
      const data = await getAssignmentMetrics();
      setGlobalMetrics(data);
    } catch (err) {
      console.error("Error fetching assignment metrics:", err);
    }
  }, []);

  const fetchGames = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllGames({ limit: 200 });
      setGames(data.games || []);
    } catch (err) {
      console.error("Error fetching games:", err);
      setError("Failed to load games");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAssignments = useCallback(async (gameId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllAssignments({ gameId });
      setAssignments(data.assignments || []);
      setPlayProgressStats(
        data.playProgressStats || { played: 0, notStarted: 0, inProgress: 0 }
      );
    } catch (err) {
      console.error("Error fetching assignments:", err);
      setError("Failed to load assignments");
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGames();
    fetchMetrics();
  }, [fetchGames, fetchMetrics]);

  useEffect(() => {
    if (selectedGame) {
      fetchAssignments(getGameId(selectedGame));
      setProgressFilter("all");
    }
  }, [selectedGame, fetchAssignments]);

  const visibleGames = games.filter((game) => {
    if (typeFilter === "all") return true;
    return resolvePlaytestTypeId(game) === typeFilter;
  });

  const filteredAssignments = assignments.filter((assignment) => {
    if (progressFilter === "all") return true;
    return derivePlayProgress(assignment) === progressFilter;
  });

  const handleRemoveAssignment = async (assignmentId) => {
    if (!window.confirm("Are you sure you want to remove this assignment?")) {
      return;
    }

    try {
      setLoading(true);
      await removeAssignment(assignmentId, "Removed by admin");
      alert("Assignment removed successfully!");
      fetchAssignments(getGameId(selectedGame));
      fetchMetrics();
    } catch (err) {
      console.error("Error removing assignment:", err);
      alert("Failed to remove assignment");
    } finally {
      setLoading(false);
    }
  };

  const handlePauseAssignment = async (assignmentId, currentStatus) => {
    const newStatus = currentStatus === "in_progress" ? "assigned" : "in_progress";

    try {
      setLoading(true);
      await updateAssignmentStatus(assignmentId, newStatus);
      alert(`Assignment ${newStatus === "assigned" ? "paused" : "resumed"} successfully!`);
      fetchAssignments(getGameId(selectedGame));
      fetchMetrics();
    } catch (err) {
      console.error("Error updating assignment status:", err);
      alert("Failed to update assignment status");
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublicPlaytest = async () => {
    const gameId = getGameId(selectedGame);
    if (!gameId) {
      return;
    }

    const nextEnabled = !selectedGame.isPublicPlaytest;

    try {
      setPublicPlaytestUpdating(true);
      const response = await setGamePublicPlaytest(gameId, nextEnabled);
      const enabled = response?.isPublicPlaytest ?? nextEnabled;

      setSelectedGame((prev) => (prev ? { ...prev, isPublicPlaytest: enabled } : prev));
      setGames((prev) =>
        prev.map((game) =>
          String(getGameId(game)) === String(gameId)
            ? { ...game, isPublicPlaytest: enabled }
            : game
        )
      );
    } catch (err) {
      console.error("Error updating public playtest flag:", err);
      alert("Failed to update Public Playtest setting");
    } finally {
      setPublicPlaytestUpdating(false);
    }
  };

  const openRemindModal = (payload) => {
    setRemindMessage("");
    setRemindForce(false);
    setRemindModal(payload);
  };

  const handleSendReminders = async () => {
    if (!remindModal) return;

    try {
      setReminding(true);
      const result = await remindAssignments({
        assignmentIds: remindModal.assignmentIds,
        gameId: remindModal.gameId,
        customMessage: remindMessage.trim() || undefined,
        force: remindForce,
      });

      const failureHints = (result.failures || [])
        .slice(0, 5)
        .map((f) => `#${f.assignmentId}: ${f.reason}`)
        .join("\n");

      alert(
        `Reminders sent: ${result.sent}\nSkipped: ${result.skipped}` +
          (failureHints ? `\n\nDetails:\n${failureHints}` : "")
      );

      setRemindModal(null);
      fetchAssignments(getGameId(selectedGame));
      fetchMetrics();
    } catch (err) {
      console.error("Error sending reminders:", err);
      alert(err.response?.data?.message || "Failed to send reminders");
    } finally {
      setReminding(false);
    }
  };

  const getProgressBadgeClass = (progress) => {
    switch (progress) {
      case "played":
      case "completed":
        return "status-completed";
      case "in_progress":
        return "status-active";
      case "not_started":
        return "status-paused";
      case "cancelled":
        return "status-cancelled";
      default:
        return "";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const playtimeFor = (game, assignment = null) =>
    getGamePlaytime({
      ...game,
      sessionHours: assignment?.sessionHours ?? game?.sessionHours,
      lockTotalPlaytime: assignment?.lockTotalPlaytime ?? game?.lockTotalPlaytime,
      numPlaytesters: assignment?.numPlaytesters ?? game?.numPlaytesters,
      playtestType: assignment?.playtestType || game?.playtestType,
    });

  const notStartedCount = playProgressStats.notStarted || 0;
  const globalPlay = globalMetrics?.playProgress;

  return (
    <div className="game-assignments-manager">
      <div className="manager-header">
        <h2>Game Assignments</h2>
        <p>View and manage playtester assignments for private playtests</p>
        {globalPlay && (
          <div className="global-progress-summary" aria-label="Global play progress">
            <span className="global-chip played">Played: {globalPlay.played || 0}</span>
            <span className="global-chip not-started">
              Not started: {globalPlay.notStarted || 0}
            </span>
            <span className="global-chip in-progress">
              In progress: {globalPlay.inProgress || 0}
            </span>
          </div>
        )}
      </div>

      <div className="manager-content">
        <div className="games-sidebar">
          <h3>Games</h3>
          <select
            className="type-filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            aria-label="Filter by playtest type"
          >
            <option value="all">All types</option>
            <option value="free_public">Free public</option>
            <option value="free_private">Free private</option>
            <option value="paid_private">Paid private</option>
          </select>
          {loading && !selectedGame && <div className="loading">Loading games...</div>}
          {error && !selectedGame && <div className="error">{error}</div>}

          <div className="games-list">
            {visibleGames.map((game) => (
              <div
                key={String(getGameId(game))}
                className={`game-item ${
                  String(getGameId(selectedGame)) === String(getGameId(game)) ? "active" : ""
                }`}
                onClick={() => setSelectedGame(game)}
              >
                <div className="game-name">{game.name}</div>
                <div className="game-info">
                  <span className="game-genre">{game.genre}</span>
                  <span className="game-status">{game.status}</span>
                  {getPlaytestTypeLabel(game) && (
                    <span
                      className={`playtest-type-badge playtest-type-badge--${resolvePlaytestTypeId(
                        game
                      )}`}
                    >
                      {getPlaytestTypeLabel(game)}
                    </span>
                  )}
                </div>
                <div className="game-hours">{playtimeFor(game).summaryLabel}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="assignments-view">
          {!selectedGame ? (
            <div className="no-selection">
              <div className="no-selection-icon">🎯</div>
              <h3>Select a Game</h3>
              <p>Choose a game from the sidebar to view and manage assignments</p>
            </div>
          ) : (
            <>
              <div className="view-header">
                <div className="game-title">
                  <h3>{selectedGame.name}</h3>
                  {getPlaytestTypeLabel(selectedGame) && (
                    <span
                      className={`playtest-type-badge playtest-type-badge--${resolvePlaytestTypeId(
                        selectedGame
                      )}`}
                    >
                      {getPlaytestTypeLabel(selectedGame)}
                    </span>
                  )}
                  <span className="assignments-count">
                    {assignments.length}{" "}
                    {assignments.length === 1 ? "Playtester" : "Playtesters"} Assigned
                  </span>
                  <span className="playtime-summary">
                    {playtimeFor(selectedGame).summaryLabel}
                  </span>
                </div>
                <div className="view-header-actions">
                  <label
                    className={`public-playtest-toggle ${
                      selectedGame.isPublicPlaytest ? "enabled" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={!!selectedGame.isPublicPlaytest}
                      onChange={handleTogglePublicPlaytest}
                      disabled={publicPlaytestUpdating}
                    />
                    <span>
                      {publicPlaytestUpdating
                        ? "Updating..."
                        : selectedGame.isPublicPlaytest
                        ? "On Public Playtest"
                        : "Add to Public Playtest"}
                    </span>
                  </label>
                  {isAdminAssignablePlaytest(selectedGame) ? (
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowAssignModal(true)}
                    >
                      Assign New Playtester
                    </button>
                  ) : (
                    <span className="public-assign-hint">
                      Public playtests are opt-in. Playtesters join themselves.
                    </span>
                  )}
                </div>
              </div>

              {assignments.length > 0 && (
                <div className="progress-toolbar">
                  <div className="progress-chips" role="tablist" aria-label="Play progress filter">
                    <button
                      type="button"
                      className={`progress-chip ${progressFilter === "all" ? "active" : ""}`}
                      onClick={() => setProgressFilter("all")}
                    >
                      All ({assignments.length})
                    </button>
                    <button
                      type="button"
                      className={`progress-chip played ${
                        progressFilter === "played" ? "active" : ""
                      }`}
                      onClick={() => setProgressFilter("played")}
                    >
                      Played ({playProgressStats.played || 0})
                    </button>
                    <button
                      type="button"
                      className={`progress-chip not-started ${
                        progressFilter === "not_started" ? "active" : ""
                      }`}
                      onClick={() => setProgressFilter("not_started")}
                    >
                      Not started ({playProgressStats.notStarted || 0})
                    </button>
                    <button
                      type="button"
                      className={`progress-chip in-progress ${
                        progressFilter === "in_progress" ? "active" : ""
                      }`}
                      onClick={() => setProgressFilter("in_progress")}
                    >
                      In progress ({playProgressStats.inProgress || 0})
                    </button>
                  </div>
                  {notStartedCount > 0 && (
                    <button
                      type="button"
                      className="btn btn-secondary remind-all-btn"
                      onClick={() =>
                        openRemindModal({
                          gameId: getGameId(selectedGame),
                          label: `all ${notStartedCount} not-started playtester(s)`,
                        })
                      }
                    >
                      Remind all not started
                    </button>
                  )}
                </div>
              )}

              {loading ? (
                <div className="loading">Loading assignments...</div>
              ) : assignments.length === 0 ? (
                <div className="no-assignments">
                  {isPublicPlaytestGame(selectedGame) ? (
                    <p>
                      This is a public playtest. Playtesters join from the Public Playtest
                      tab.
                    </p>
                  ) : (
                    <>
                      <p>No playtesters assigned to this game yet.</p>
                      <button
                        className="btn btn-primary"
                        onClick={() => setShowAssignModal(true)}
                      >
                        Assign First Playtester
                      </button>
                    </>
                  )}
                </div>
              ) : filteredAssignments.length === 0 ? (
                <div className="no-assignments">
                  <p>No playtesters match this progress filter.</p>
                </div>
              ) : (
                <div className="assignments-table">
                  <div className="table-header">
                    <div className="col-player">Playtester</div>
                    <div className="col-hours">Hours assigned</div>
                    <div className="col-sessions">Sessions</div>
                    <div className="col-validation">Validation Breakdown</div>
                    <div className="col-status">Status</div>
                    <div className="col-dates">Dates</div>
                    <div className="col-actions">Actions</div>
                  </div>

                  {filteredAssignments.map((assignment) => {
                    const playtime = playtimeFor(selectedGame, assignment);
                    const progress = derivePlayProgress(assignment);
                    return (
                      <div key={assignment.assignmentId} className="table-row">
                        <div className="col-player">
                          <div className="player-name">{assignment.playtesterName}</div>
                          {assignment.playtesterUsername && (
                            <div className="player-username">
                              @{assignment.playtesterUsername}
                            </div>
                          )}
                          <div className="player-id">ID: {assignment.playtesterId}</div>
                        </div>

                        <div className="col-hours">
                          <div className="hours-assigned">{playtime.perPlayerLabel}</div>
                          {playtime.lockTotalPlaytime && playtime.configured && (
                            <div className="hours-note">Share of total</div>
                          )}
                        </div>

                        <div className="col-sessions">
                          <div className="sessions-count">
                            {assignment.sessionsCompleted || 0} Total
                          </div>
                        </div>

                        <div className="col-validation">
                          {assignment.sessionValidation ? (
                            <div className="validation-breakdown">
                              <div className="validation-item approved">
                                <span className="validation-icon">✅</span>
                                <span className="validation-count">
                                  {assignment.sessionValidation.approved || 0}
                                </span>
                                <span className="validation-label">Approved</span>
                              </div>
                              <div className="validation-item rejected">
                                <span className="validation-icon">❌</span>
                                <span className="validation-count">
                                  {assignment.sessionValidation.rejected || 0}
                                </span>
                                <span className="validation-label">Rejected</span>
                              </div>
                              <div className="validation-item flagged">
                                <span className="validation-icon">⚠️</span>
                                <span className="validation-count">
                                  {assignment.sessionValidation.flagged || 0}
                                </span>
                                <span className="validation-label">Flagged</span>
                              </div>
                              <div className="validation-item pending">
                                <span className="validation-icon">⏳</span>
                                <span className="validation-count">
                                  {assignment.sessionValidation.pending || 0}
                                </span>
                                <span className="validation-label">Pending</span>
                              </div>
                            </div>
                          ) : (
                            <span className="no-data">No session data</span>
                          )}
                        </div>

                        <div className="col-status">
                          <span className={`status-badge ${getProgressBadgeClass(progress)}`}>
                            {PLAY_PROGRESS_LABELS[progress] || progress}
                          </span>
                        </div>

                        <div className="col-dates">
                          <div className="date-info">
                            <span className="date-label">Assigned:</span>
                            <span className="date-value">
                              {formatDate(assignment.assignedAt)}
                            </span>
                          </div>
                          {assignment.lastActivity && (
                            <div className="date-info">
                              <span className="date-label">Last Active:</span>
                              <span className="date-value">
                                {formatDate(assignment.lastActivity)}
                              </span>
                            </div>
                          )}
                          {assignment.lastRemindedAt && (
                            <div className="date-info">
                              <span className="date-label">Last Reminder:</span>
                              <span className="date-value">
                                {formatDate(assignment.lastRemindedAt)}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="col-actions">
                          {progress === "not_started" && (
                            <button
                              className="action-btn remind-btn"
                              onClick={() =>
                                openRemindModal({
                                  assignmentIds: [assignment.assignmentId],
                                  label:
                                    assignment.playtesterName ||
                                    assignment.playtesterUsername ||
                                    `playtester #${assignment.playtesterId}`,
                                })
                              }
                              title="Send play reminder email"
                            >
                              📧
                            </button>
                          )}
                          <button
                            className="action-btn pause-btn"
                            onClick={() =>
                              handlePauseAssignment(assignment.assignmentId, assignment.status)
                            }
                            title={
                              assignment.status === "in_progress"
                                ? "Pause assignment"
                                : "Resume assignment"
                            }
                          >
                            {assignment.status === "in_progress" ? "⏸️" : "▶️"}
                          </button>
                          <button
                            className="action-btn remove-btn"
                            onClick={() => handleRemoveAssignment(assignment.assignmentId)}
                            title="Remove assignment"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showAssignModal && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Assign Playtester to {selectedGame.name}</h3>
              <button className="modal-close" onClick={() => setShowAssignModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Use the Manual Assignment tab to assign playtesters to this game.</p>
              <button className="btn btn-primary" onClick={() => setShowAssignModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {remindModal && (
        <div className="modal-overlay" onClick={() => !reminding && setRemindModal(null)}>
          <div className="modal-content remind-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Send play reminder</h3>
              <button
                className="modal-close"
                onClick={() => !reminding && setRemindModal(null)}
                disabled={reminding}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Email a reminder to <strong>{remindModal.label}</strong>. Only not-started
                assignees who allow assignment emails will be contacted.
              </p>
              <label className="remind-message-label" htmlFor="remind-message">
                Optional message
              </label>
              <textarea
                id="remind-message"
                className="remind-message"
                rows={4}
                value={remindMessage}
                onChange={(e) => setRemindMessage(e.target.value)}
                placeholder="Add a short note for the playtester…"
                disabled={reminding}
              />
              <label className="remind-force">
                <input
                  type="checkbox"
                  checked={remindForce}
                  onChange={(e) => setRemindForce(e.target.checked)}
                  disabled={reminding}
                />
                Force send even if reminded in the last 24 hours
              </label>
              <div className="remind-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setRemindModal(null)}
                  disabled={reminding}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSendReminders}
                  disabled={reminding}
                >
                  {reminding ? "Sending…" : "Send reminder"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameAssignmentsManager;
